import Chart from "chart.js/auto";

const chartInstances = [];

function parseDataset(root, attributeName) {
  const raw = root.getAttribute(attributeName);
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
}

function registerChart(config) {
  const chart = new Chart(config.ctx, config.options);
  chartInstances.push(chart);
  return chart;
}

function destroyDashboardCharts() {
  while (chartInstances.length > 0) {
    const chart = chartInstances.pop();
    if (chart) chart.destroy();
  }
}

function createTrendChart(root) {
  const canvas = document.getElementById("dailyEnrollmentTrendChart");
  if (!canvas) return;

  const trendData = parseDataset(root, "data-chart-trend");
  if (trendData.length === 0) return;

  const labels = trendData.map((item) => item.date);
  const values = trendData.map((item) => Number(item.total || 0));
  const context = canvas.getContext("2d");
  const gradient = context.createLinearGradient(0, 0, 0, 300);
  gradient.addColorStop(0, "rgba(212, 98, 43, 0.45)");
  gradient.addColorStop(1, "rgba(212, 98, 43, 0.04)");

  registerChart({
    ctx: context,
    options: {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Inscripciones",
            data: values,
            borderColor: "#d4622b",
            borderWidth: 3,
            backgroundColor: gradient,
            fill: true,
            tension: 0.35,
            pointRadius: 3,
            pointHoverRadius: 5,
            pointBackgroundColor: "#ffffff",
            pointBorderColor: "#d4622b",
            pointBorderWidth: 2
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { mode: "index", intersect: false }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: "#8a7e74", maxRotation: 0, autoSkipPadding: 12 }
          },
          y: {
            beginAtZero: true,
            grid: { color: "rgba(138, 126, 116, 0.12)" },
            ticks: { precision: 0, color: "#6a5d54" }
          }
        }
      }
    }
  });
}

function createEnrollmentChart(root) {
  const canvas = document.getElementById("enrollmentStatusChart");
  if (!canvas) return;

  const enrollmentData = parseDataset(root, "data-chart-enrollment");
  if (enrollmentData.length === 0) return;

  const filtered = enrollmentData.filter((item) => Number(item.value || 0) > 0);
  const source = filtered.length > 0 ? filtered : enrollmentData;

  registerChart({
    ctx: canvas.getContext("2d"),
    options: {
      type: "doughnut",
      data: {
        labels: source.map((item) => item.label),
        datasets: [
          {
            data: source.map((item) => Number(item.value || 0)),
            backgroundColor: ["#2d8a5d", "#e76f51", "#3d6b82", "#c0392b"],
            borderColor: "#ffffff",
            borderWidth: 3,
            hoverOffset: 8
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        cutout: "68%",
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (context) => `${context.label}: ${context.parsed}`
            }
          }
        }
      }
    }
  });
}

function createReceiptsChart(root) {
  const canvas = document.getElementById("receiptStatusChart");
  if (!canvas) return;

  const receiptData = parseDataset(root, "data-chart-receipts");
  if (receiptData.length === 0) return;

  registerChart({
    ctx: canvas.getContext("2d"),
    options: {
      type: "bar",
      data: {
        labels: receiptData.map((item) => item.label),
        datasets: [
          {
            data: receiptData.map((item) => Number(item.value || 0)),
            borderRadius: 8,
            backgroundColor: ["#f4a261", "#2a9d8f", "#c0392b", "#e9c46a"]
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: "#8a7e74" }
          },
          y: {
            beginAtZero: true,
            grid: { color: "rgba(138, 126, 116, 0.12)" },
            ticks: { precision: 0, color: "#6a5d54" }
          }
        }
      }
    }
  });
}

function createGroupsChart(root) {
  const canvas = document.getElementById("groupOccupancyChart");
  if (!canvas) return;

  const groupData = parseDataset(root, "data-chart-groups");
  if (groupData.length === 0) return;

  registerChart({
    ctx: canvas.getContext("2d"),
    options: {
      type: "bar",
      data: {
        labels: groupData.map((item) => item.label),
        datasets: [
          {
            label: "Ocupación",
            data: groupData.map((item) => Number(item.occupancy || 0)),
            borderRadius: 10,
            backgroundColor: "#2a9d8f"
          }
        ]
      },
      options: {
        indexAxis: "y",
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (context) => {
                const group = groupData[context.dataIndex];
                return `${context.parsed.x}% (${group.used}/${group.total})`;
              }
            }
          }
        },
        scales: {
          x: {
            beginAtZero: true,
            max: 100,
            grid: { color: "rgba(138, 126, 116, 0.12)" },
            ticks: {
              callback: (value) => `${value}%`,
              color: "#6a5d54"
            }
          },
          y: {
            grid: { display: false },
            ticks: { color: "#6a5d54" }
          }
        }
      }
    }
  });
}

function createWeeksChart(root) {
  const canvas = document.getElementById("weekDemandChart");
  if (!canvas) return;

  const weekData = parseDataset(root, "data-chart-weeks");
  if (weekData.length === 0) return;

  registerChart({
    ctx: canvas.getContext("2d"),
    options: {
      type: "bar",
      data: {
        labels: weekData.map((item) => item.label),
        datasets: [
          {
            label: "Selecciones",
            data: weekData.map((item) => Number(item.value || 0)),
            borderRadius: 10,
            backgroundColor: "rgba(233, 196, 106, 0.95)",
            borderColor: "#c9a227",
            borderWidth: 1.2
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: "#8a7e74" }
          },
          y: {
            beginAtZero: true,
            grid: { color: "rgba(138, 126, 116, 0.12)" },
            ticks: { precision: 0, color: "#6a5d54" }
          }
        }
      }
    }
  });
}

function createInventoryChart(root) {
  const canvas = document.getElementById("inventoryAlertChart");
  if (!canvas) return;

  const inventoryData = parseDataset(root, "data-chart-inventory");
  if (inventoryData.length === 0) return;

  registerChart({
    ctx: canvas.getContext("2d"),
    options: {
      data: {
        labels: inventoryData.map((item) => item.label),
        datasets: [
          {
            type: "bar",
            label: "Existencias",
            data: inventoryData.map((item) => Number(item.stock || 0)),
            borderRadius: 8,
            backgroundColor: "rgba(231, 111, 81, 0.82)"
          },
          {
            type: "line",
            label: "Mínimo",
            data: inventoryData.map((item) => Number(item.minimum || 0)),
            borderColor: "#264653",
            backgroundColor: "#264653",
            tension: 0.3,
            pointRadius: 4,
            pointHoverRadius: 5
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "bottom",
            labels: { usePointStyle: true, boxWidth: 8, color: "#4a3f37" }
          }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: "#8a7e74" }
          },
          y: {
            beginAtZero: true,
            grid: { color: "rgba(138, 126, 116, 0.12)" },
            ticks: { precision: 0, color: "#6a5d54" }
          }
        }
      }
    }
  });
}

function initializeDashboardCharts() {
  const root = document.querySelector(".anamary-dashboard[data-dashboard-charts='true']");
  if (!root) return;

  createTrendChart(root);
  createEnrollmentChart(root);
  createReceiptsChart(root);
  createGroupsChart(root);
  createWeeksChart(root);
  createInventoryChart(root);
}

document.addEventListener("turbolinks:load", () => {
  destroyDashboardCharts();
  initializeDashboardCharts();
});

document.addEventListener("turbolinks:before-cache", () => {
  destroyDashboardCharts();
});
